/** Critically damped motion: continuous velocity, no overshoot, independent of refresh rate. */
export interface Motion {
  position: number;
  velocity: number;
}

export interface AnimationClock {
  now(): number;
  request(callback: (now: number) => void): number;
  cancel(handle: number): void;
}

/** A settled surface consumes no animation frames. Waking never includes idle time in its first step. */
export function createAnimationDriver(
  update: (now: number, seconds: number) => boolean,
  clock: AnimationClock,
) {
  let pending: number | null = null,
    previous = clock.now(),
    suspended = false,
    disposed = false;
  const frame = (now: number) => {
    pending = null;
    if (disposed || suspended) return;
    const again = update(now, (now - previous) / 1000);
    previous = now;
    if (again && !disposed && !suspended && pending === null)
      pending = clock.request(frame);
  };
  const wake = () => {
    if (disposed || suspended || pending !== null) return;
    previous = clock.now();
    pending = clock.request(frame);
  };
  const cancel = () => {
    if (pending !== null) clock.cancel(pending);
    pending = null;
  };
  return {
    wake,
    setSuspended(value: boolean) {
      suspended = value;
      if (value) cancel();
      else wake();
    },
    dispose() {
      disposed = true;
      cancel();
    },
  };
}
export function stepMotion(
  current: Motion,
  target: number,
  seconds: number,
  response = 0.24,
): Motion {
  if (!Number.isFinite(target)) return current;
  const dt = Math.max(0, Math.min(0.1, seconds));
  if (!dt) return current;
  const omega = 2 / Math.max(0.06, response),
    distance = current.position - target;
  const decay = Math.exp(-omega * dt),
    change = (current.velocity + omega * distance) * dt;
  let position = target + (distance + change) * decay,
    velocity = (current.velocity - omega * change) * decay;
  // Re-targeting may leave residual velocity pointing beyond the destination.
  if ((target - current.position) * (target - position) < 0) {
    position = target;
    velocity = 0;
  }
  if (Math.abs(target - position) < 0.025 && Math.abs(velocity) < 0.025) {
    position = target;
    velocity = 0;
  }
  return { position, velocity };
}
